import React, { useRef, useState, useEffect, ReactNode } from 'react';
import { Box, SxProps, Theme } from '@mui/material';

interface FadeInSectionProps {
  children: ReactNode;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'scale';
  threshold?: number;
  sx?: SxProps<Theme>;
  className?: string;
}

/**
 * A wrapper component that fades in its children when they scroll into view.
 */
const FadeInSection: React.FC<FadeInSectionProps> = ({
  children,
  delay = 0,
  direction = 'up',
  threshold = 0.1,
  sx,
  className,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold: 0, rootMargin: '50px 0px 0px 0px' }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold]);

  const getTransform = () => {
    switch (direction) {
      case 'up':
        return 'translateY(16px)';
      case 'down':
        return 'translateY(-16px)';
      case 'left':
        return 'translateX(16px)';
      case 'right':
        return 'translateX(-16px)';
      case 'scale':
        return 'scale(0.9)';
      default:
        return 'translateY(30px)';
    }
  };

  return (
    <Box
      ref={ref}
      className={className}
      sx={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) translateX(0) scale(1)' : getTransform(),
        transition: `opacity 0.3s ease-out ${delay}ms, transform 0.3s ease-out ${delay}ms`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default FadeInSection;
